import { useCallback } from "react";
import { useEffectEvent } from "@/hooks/useEffectEvent";
import { useAppDispatch } from "@/hooks/redux";
import { projectActions } from "@/redux/stores/projectStore";
import { useCheckDevice } from "@/hooks/useCheckDevice";
import { useTranslation } from "next-i18next";
import { useSubscription } from "@/hooks/useSubscription";
import { MODAL_NAME } from "@/components/editor/stripe/constants";

export function useSubscriptionModal() {
  const dispatch = useAppDispatch();
  const { isMobile } = useCheckDevice();
  const { t } = useTranslation();

  const { plan } = useSubscription();

  const openCreditModal = useCallback((params: {
    type?: "limited" | "locked";
    message?: string[];
  }) => {
    if (isMobile) {
      dispatch(projectActions.setOpenMobileSubscriptionModal(true));
      return;
    }
    
    const payload = (() => {
      const modalTitle = (() => {
        if (params.type === "limited") {
          return t("MAXIMUM LIMIT");
        }

        return t("LOCKED FEATURE");
      })();

      const modalLogoUrl = (() => {
        if (params.type === "limited") {
          return "/images/modal/material-symbols_folder-limited.png";
        }

        return "/images/modal/mdi_lock.png";
      })();

      const noticeTitle = (() => {
        if (params.type === "limited") {
          return t("You’ve reached the maximum limit");
        }

        return t("You’ve discovered a locked feature");
      })();

      const noticeExplains = (() => {
        if (params.message) {
          return params.message;
        }

        if (params.type === "limited") {
          return [t("By upgrading your plan, you can continue exporting more videos.")];
        }

        return [t("By upgrading your plan, you'll unlock access to even more powerful features.")];
      })();

      return {
        currentPlan: plan?.raw,
        openedModal: MODAL_NAME.NOTICE,
        modalTitle,
        modalLogoUrl,
        noticeTitle,
        noticeExplains,
      }
    })();
    
    dispatch(projectActions.setNoticeModalCredit(payload));
  }, [dispatch, isMobile, plan?.raw, t]);

  const openPlanModal = useCallback(() => {
    if (isMobile) {
      dispatch(projectActions.setOpenMobileSubscriptionModal(true))
    } else {
      dispatch(projectActions.setCurrentPlanAndOpenedModal({
        currentPlan: plan?.raw,
        openedModal: MODAL_NAME.SUBSCRIPTION_LIST,
      }));
    }
  }, [dispatch, isMobile, plan?.raw]);

  const openPlanSwitchModal = useCallback(() => {
    dispatch(
      projectActions.setCurrentPlanAndOpenedModal({
        currentPlan: plan?.raw,
        openedModal: MODAL_NAME.PLAN_SWITCH,
      }),
    );
  }, [dispatch, plan?.raw]);

  const onOpenCreditModal = useEffectEvent((params: {
    type?: "limited" | "locked";
    message?: string[];
  }) => openCreditModal(params));

  const onOpenPlanModal = useEffectEvent(() => openPlanModal());

  const onOpenPlanSwitchModal = useEffectEvent(() => openPlanSwitchModal());

  return {
    open: onOpenCreditModal,
    openCreditModal: onOpenCreditModal,
    openPlanModal: onOpenPlanModal,
    openPlanSwitchModal: onOpenPlanSwitchModal,
  };
}

export type OpenSubscriptionModal = ReturnType<typeof useSubscriptionModal>["open"];